import type {
  ArticleCategory,
  ArticleCreate,
  ArticleDetail,
  ArticleListItem,
  ArticleUnit,
  ArticleUnitUpsert,
  ArticleUpdate,
  AuditHistoryResponse,
  BarcodeCreate,
  BarcodeListItem,
  BarcodeResolution,
  BarcodeUpdate,
  ListResponse,
  Restaurant,
  RestaurantCreate,
  RestaurantDetail,
  RestaurantUpdate,
  RestaurantUser,
  RestaurantUserCandidate,
  Supplier,
  SupplierCreate,
  SupplierDetail,
  SupplierUpdate,
  Unit,
} from '@warehouse/shared';

import { api } from '../../lib/api-client';
import { listQueryParams, type ListState } from '../../lib/list-query';
import { commandId } from '../../lib/command-id';

/**
 * The master-data HTTP surface, typed from the SHARED contracts — the same zod
 * schemas the API parses with, so a field renamed on the server is a compile
 * error here rather than an `undefined` in a table cell.
 *
 * Every mutation sends `X-Command-Id`: a client-generated uuid that makes a
 * retry after a dropped response a no-op instead of a duplicate row
 * (`command_dedupe`, ARCHITECTURE §4) — the same guarantee the offline scanner
 * outbox relies on.
 */

function commandHeaders(): Record<string, string> {
  return { 'X-Command-Id': commandId() };
}

export interface CategoryListRow extends ArticleCategory {
  articleCount: number;
}

export interface RestaurantListRow extends Restaurant {
  userCount: number;
}

// --- articles ---------------------------------------------------------------

export const listArticles = (state: ListState): Promise<ListResponse<ArticleListItem>> =>
  api.get('/articles', { query: listQueryParams(state) });

export const getArticle = (id: string): Promise<ArticleDetail> => api.get(`/articles/${id}`);

export const getArticleHistory = (id: string): Promise<AuditHistoryResponse> =>
  api.get(`/articles/${id}/history`);

export const createArticle = (body: ArticleCreate): Promise<ArticleDetail> =>
  api.post('/articles', body, { headers: commandHeaders() });

export const updateArticle = (id: string, body: ArticleUpdate): Promise<ArticleDetail> =>
  api.patch(`/articles/${id}`, body, { headers: commandHeaders() });

export const upsertArticleUnit = (
  articleId: string,
  body: ArticleUnitUpsert,
): Promise<ArticleUnit[]> =>
  api.post(`/articles/${articleId}/units`, body, { headers: commandHeaders() });

export const removeArticleUnit = (articleId: string, articleUnitId: string): Promise<ArticleUnit[]> =>
  api.delete(`/articles/${articleId}/units/${articleUnitId}`, { headers: commandHeaders() });

// --- units ------------------------------------------------------------------

export const listUnits = (state: ListState): Promise<ListResponse<Unit>> =>
  api.get('/units', { query: listQueryParams(state) });

export const createUnit = (body: { code: string; name: string }): Promise<Unit> =>
  api.post('/units', body, { headers: commandHeaders() });

export const updateUnit = (id: string, body: { code?: string; name?: string }): Promise<Unit> =>
  api.patch(`/units/${id}`, body, { headers: commandHeaders() });

export const removeUnit = (id: string): Promise<{ id: string }> =>
  api.delete(`/units/${id}`, { headers: commandHeaders() });

// --- categories -------------------------------------------------------------

export const listCategories = (state: ListState): Promise<ListResponse<CategoryListRow>> =>
  api.get('/article-categories', { query: listQueryParams(state) });

export const createCategory = (body: unknown): Promise<ArticleCategory> =>
  api.post('/article-categories', body, { headers: commandHeaders() });

export const updateCategory = (id: string, body: unknown): Promise<ArticleCategory> =>
  api.patch(`/article-categories/${id}`, body, { headers: commandHeaders() });

export const removeCategory = (id: string): Promise<{ id: string }> =>
  api.delete(`/article-categories/${id}`, { headers: commandHeaders() });

// --- barcodes ---------------------------------------------------------------

export const listBarcodes = (state: ListState): Promise<ListResponse<BarcodeListItem>> =>
  api.get('/barcodes', { query: listQueryParams(state) });

export const createBarcode = (body: BarcodeCreate): Promise<BarcodeListItem> =>
  api.post('/barcodes', body, { headers: commandHeaders() });

export const teachBarcode = (body: BarcodeCreate & { raw: string }): Promise<BarcodeListItem> =>
  api.post('/barcodes/teach', body, { headers: commandHeaders() });

export const updateBarcode = (id: string, body: BarcodeUpdate): Promise<BarcodeListItem> =>
  api.patch(`/barcodes/${id}`, body, { headers: commandHeaders() });

export const removeBarcode = (id: string): Promise<{ id: string }> =>
  api.delete(`/barcodes/${id}`, { headers: commandHeaders() });

export const resolveBarcode = (
  raw: string,
  supplierId?: string | null,
): Promise<BarcodeResolution> =>
  api.post('/barcodes/resolve', { raw, supplierId: supplierId ?? null });

// --- suppliers --------------------------------------------------------------

export const listSuppliers = (state: ListState): Promise<ListResponse<Supplier>> =>
  api.get('/suppliers', { query: listQueryParams(state) });

export const getSupplier = (id: string): Promise<SupplierDetail> => api.get(`/suppliers/${id}`);

export const getSupplierHistory = (id: string): Promise<AuditHistoryResponse> =>
  api.get(`/suppliers/${id}/history`);

export const createSupplier = (body: SupplierCreate): Promise<SupplierDetail> =>
  api.post('/suppliers', body, { headers: commandHeaders() });

export const updateSupplier = (id: string, body: SupplierUpdate): Promise<SupplierDetail> =>
  api.patch(`/suppliers/${id}`, body, { headers: commandHeaders() });

// --- restaurants ------------------------------------------------------------

export const listRestaurants = (state: ListState): Promise<ListResponse<RestaurantListRow>> =>
  api.get('/restaurants', { query: listQueryParams(state) });

export const getRestaurant = (id: string): Promise<RestaurantDetail> => api.get(`/restaurants/${id}`);

export const getRestaurantHistory = (id: string): Promise<AuditHistoryResponse> =>
  api.get(`/restaurants/${id}/history`);

export const getRestaurantUsers = (id: string): Promise<RestaurantUser[]> =>
  api.get(`/restaurants/${id}/users`);

export const createRestaurant = (body: RestaurantCreate): Promise<Restaurant> =>
  api.post('/restaurants', body, { headers: commandHeaders() });

export const updateRestaurant = (id: string, body: RestaurantUpdate): Promise<Restaurant> =>
  api.patch(`/restaurants/${id}`, body, { headers: commandHeaders() });

// --- restaurant users -------------------------------------------------------

export const listRestaurantUsers = (q?: string): Promise<RestaurantUser[]> =>
  api.get('/restaurant-users', { query: q ? { q } : undefined });

export const listUserCandidates = (q?: string): Promise<RestaurantUserCandidate[]> =>
  api.get('/restaurant-users/candidates', { query: q ? { q } : undefined });

export const linkRestaurantUser = (body: {
  userId: string;
  restaurantId: string;
}): Promise<RestaurantUser[]> =>
  api.post('/restaurant-users', body, { headers: commandHeaders() });

export const unlinkRestaurantUser = (
  restaurantId: string,
  userId: string,
): Promise<RestaurantUser[]> =>
  api.delete(`/restaurant-users/${restaurantId}/${userId}`, { headers: commandHeaders() });
